import type { Entry } from '@taler/api-client'
import { addMoney, type LocalDate, type Money } from '@taler/domain'

export interface ContactHistoryRow {
  readonly id: string
  readonly date: LocalDate
  readonly title: string
  /** The contact's own posting: positive owes you, negative you owe. */
  readonly amount: Money
}

/**
 * One row per entry and currency the contact's account is posted in, carrying the sum of that
 * entry's postings on the account in that currency. The contact account accepts any currency, so
 * one entry can carry postings in several currencies; they are never summed across currencies.
 */
export function toContactHistoryRows(
  entries: readonly Entry[],
  contactAccountId: string,
): readonly ContactHistoryRow[] {
  return entries.flatMap((entry) => {
    const totals = new Map<string, Money>()
    for (const posting of entry.postings) {
      if (posting.accountId !== contactAccountId) continue
      const current = totals.get(posting.amount.currency)
      totals.set(
        posting.amount.currency,
        current === undefined ? posting.amount : addMoney(current, posting.amount),
      )
    }
    return [...totals.values()].map((amount) => ({
      id: entry.id,
      date: entry.date,
      title: entry.payee ?? entry.memo,
      amount,
    }))
  })
}
